import { EnvironmentType } from '../schema/environment.enum';

/**
 * PostHog — per-environment browser configuration for the portal.
 *
 * `token` is the PostHog **Project token** (Settings → Project → General →
 * "Project token", starts with `phc_`). It is browser-safe by design: every
 * visitor's browser sends it with each event. It is NOT a secret.
 *
 * NEVER put a Personal API key (`phx_…`) or a Project secret key (`phs_…`)
 * here — those grant read/write access to the whole project. The service
 * refuses any token that does not start with `phc_` (analytics is disabled
 * rather than a private key leaked).
 *
 * The Khoyek MVP handoff («تدقيق ربط PostHog», 7 Oct 2026) puts the app, the
 * `/kalma` web page and the server in ONE project on **EU Cloud**, all sharing
 * the `u_<user_id>` identity — so this portal targets the EU ingestion host.
 *
 * Status (2026-10-14): the EU project token is supplied → PostHog is ON in
 * DEV / STAGING / UAT / LIVE / PRODUCTION. To turn it off, empty
 * `TALBINAH_EU_PROJECT.token` (one line) — the SDK is then never downloaded.
 *
 * LOCAL / IN_MEMORY / TEST / RE_DESIGN are absent on purpose — unit tests and
 * laptops must never report. `localhost` also stays silent unless
 * `localStorage['tb-ph-local'] = '1'`.
 */
export interface PosthogEnvConfig {
  /** Project token (`phc_…`). Empty string = disabled. */
  token: string;
  /** Ingestion host for the project's region. */
  apiHost: string;
}

/** The only token kind that may ever reach a browser bundle. */
export const POSTHOG_PROJECT_TOKEN_PREFIX = 'phc_';

/** `localStorage` key that lets a developer opt localhost IN. */
export const POSTHOG_LOCAL_OVERRIDE_KEY = 'tb-ph-local';

/** posthog-js dated defaults in force (history-change pageviews, SSR-safe script injection). */
export const POSTHOG_SDK_DEFAULTS = '2026-05-30';

/** Super-property `platform` on every event (handoff §2.4: every event carries platform). */
export const POSTHOG_PLATFORM = 'web';

/**
 * Super-property `app_name` on every event — tells this portal apart from the
 * Laravel `/kalma` page, which also reports `platform = 'web'` to the same project.
 * Event-only on purpose: it is not in the handoff's person-property list (§2.2).
 */
export const POSTHOG_APP_NAME = 'talbinah-reference-portal';

const EU_API_HOST = 'https://eu.i.posthog.com';

/** The Talbinah EU project (see the status note above). */
const TALBINAH_EU_PROJECT: PosthogEnvConfig = {
  token: 'phc_zDnxx8ccqNJbtHTrEs38jXbe8huStmmUvYovF99LacXj',
  apiHost: EU_API_HOST,
};

export const POSTHOG: Partial<Record<EnvironmentType, PosthogEnvConfig>> = {
  [EnvironmentType.DEV]: TALBINAH_EU_PROJECT,
  [EnvironmentType.STAGING]: TALBINAH_EU_PROJECT,
  [EnvironmentType.UAT]: TALBINAH_EU_PROJECT,
  [EnvironmentType.LIVE]: TALBINAH_EU_PROJECT,
  [EnvironmentType.PRODUCTION]: TALBINAH_EU_PROJECT,
};
